import { useEffect, useMemo, useState } from "react";
import {
  Wallet,
  Phone,
  Briefcase,
  Calendar,
  RefreshCw,
  LogIn,
  LogOut,
  Download,
  IdCard,
  Fish,
  CircleDollarSign,
  ScrollText,
  Home,
  Scale,
} from "lucide-react";
import { toast } from "sonner";

import LoginModal from "../components/dashboard/LoginModal";
import FedinaPenale from "../components/dashboard/FedinaPenale";
import ContoCorrente from "../components/dashboard/ContoCorrente";
import VitaLavorativa from "../components/dashboard/VitaLavorativa";
import { useAuth, apiClient, API } from "../context/AuthContext";

/** Saluto in base all'ora */
function greeting() {
  const h = new Date().getHours();
  if (h >= 8 && h < 14) return "Buongiorno";
  if (h >= 14 && h < 18) return "Buon pomeriggio";
  if (h >= 18) return "Buonasera";
  return "Buonanotte";
}

const NAV = [
  { id: "home", label: "Generale", icon: Home },
  { id: "conto", label: "Conto corrente", icon: Wallet },
  { id: "lavoro", label: "Vita lavorativa", icon: Briefcase },
  { id: "fedina", label: "Fedina penale", icon: Scale },
];

function StatTile({ icon: Icon, label, value, tint = "blue", testid }) {
  const tints = {
    blue: "bg-[hsl(var(--brand-500)/0.18)] text-[hsl(var(--brand-300))] border-[hsl(var(--brand-500)/0.4)]",
    cyan: "bg-[hsl(var(--accent-cyan)/0.16)] text-[hsl(var(--accent-cyan))] border-[hsl(var(--accent-cyan)/0.35)]",
    amber: "bg-[hsl(40_92%_55%/0.16)] text-[hsl(40_95%_72%)] border-[hsl(40_92%_55%/0.32)]",
    slate: "bg-[hsl(220_10%_40%/0.20)] text-[hsl(220_15%_82%)] border-[hsl(220_10%_40%/0.35)]",
  }[tint];
  return (
    <div className="tile p-5 flex flex-col gap-3 min-h-[120px]" data-testid={testid}>
      <div className="flex items-center gap-2.5">
        <span className={`inline-flex w-8 h-8 rounded-md border items-center justify-center ${tints}`}>
          <Icon className="w-4 h-4" />
        </span>
        <span className="text-[hsl(var(--text-soft))] text-sm">{label}</span>
      </div>
      <div className="text-2xl font-semibold text-white leading-none truncate">
        {value ?? <span className="text-[hsl(var(--text-mute))]">Non disponibile</span>}
      </div>
    </div>
  );
}

function firstPresent(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

function formatMoney(value) {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return `${n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function SectionHead({ title, subtitle, icon: Icon }) {
  return (
    <div className="flex items-center gap-4 fade-up">
      <span className="inline-flex w-14 h-14 rounded-2xl items-center justify-center bg-gradient-to-br from-[hsl(var(--brand-300))] via-[hsl(var(--brand-500))] to-[hsl(var(--brand-700))] shadow-lg shadow-[hsl(var(--brand-500)/0.45)]">
        <Icon className="w-7 h-7 text-white" />
      </span>
      <div className="flex-1 min-w-0">
        <h2 className="font-display text-3xl sm:text-4xl tracking-wide text-white leading-none">{title}</h2>
        <p className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[hsl(var(--text-mute))] mt-2">{subtitle}</p>
        <div className="section-underline mt-3" />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [openLogin, setOpenLogin] = useState(false);
  const [stats, setStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [active, setActive] = useState("home");

  const nickname = user?.nickname;
  const avatar = useMemo(
    () => user
      ? `https://mc-heads.net/avatar/${encodeURIComponent(user.nickname)}/96`
      : `https://mc-heads.net/avatar/MHF_Steve/96`,
    [user]
  );

  const loadStats = async () => {
    if (!user) return;
    setRefreshing(true);
    try {
      const { data } = await apiClient.get("/player/me");
      setStats(data);
    } catch {
      toast.error("Impossibile caricare i dati dal server");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadStats();
    } else {
      setStats(null);
    }
    // eslint-disable-next-line
  }, [user?.nickname]);

  const downloadProject = () => window.open(`${API}/download/project`, "_blank");

  // Derived display values
  const conto = formatMoney(firstPresent(stats?.balance, stats?.conto, stats?.money, stats?.saldo));
  const lavoro = firstPresent(stats?.rank, stats?.lavoro, stats?.job, stats?.rank_raw, stats?.primary_group);
  const aragoste = firstPresent(stats?.aragoste, stats?.lobsters);
  const telefono = firstPresent(stats?.phone, stats?.telefono, stats?.phone_number);
  const tempo = firstPresent(stats?.playtime_7d, stats?.tempo, stats?.playtime);
  const docCount = stats?.documents?.length || 0;
  const docDisplay = stats ? (docCount > 0 ? `${docCount}` : "Nessuno") : null;

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      {/* ambient blobs */}
      <div aria-hidden className="pointer-events-none fixed -top-40 -left-40 w-[480px] h-[480px] rounded-full blob blob-blue z-0" />
      <div aria-hidden className="pointer-events-none fixed top-40 -right-40 w-[520px] h-[520px] rounded-full blob blob-cyan z-0" />
      <div aria-hidden className="pointer-events-none fixed inset-0 grain z-0" />

      {/* ============== TOP NAV ============== */}
      <header className="relative z-20 mx-auto max-w-7xl px-5 sm:px-8 pt-6">
        <div className="flex items-center justify-between gap-4">
          {/* brand */}
          <div className="flex items-center gap-3" data-testid="brand">
            <div className="brand-mark">
              <span className="font-display text-lg leading-none">GR</span>
            </div>
            <div className="hidden sm:block">
              <div className="font-display tracking-wider text-sm leading-none">Gestionale Roleplay</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--text-mute))] mt-1">
                v2 · Minecraft
              </div>
            </div>
          </div>

          {/* nav pills */}
          <nav className="hidden md:flex items-center gap-1 p-1.5 rounded-full bg-[hsl(var(--bg-2)/0.7)] border border-[hsl(var(--border-soft))] backdrop-blur" data-testid="topnav">
            {NAV.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActive(id)}
                data-testid={`nav-${id}`}
                className={`nav-pill ${active === id ? "nav-pill-active" : ""}`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{label}</span>
              </button>
            ))}
          </nav>

          {/* actions */}
          <div className="flex items-center gap-2.5" data-testid="topbar-actions">
            <button onClick={downloadProject} data-testid="download-project-btn" className="btn-ghost inline-flex items-center gap-2 text-sm" title="Scarica il sorgente del progetto v2">
              <Download className="w-4 h-4" /><span className="hidden sm:inline">v2 zip</span>
            </button>
            {user ? (
              <button onClick={async () => { await logout(); toast.info("Disconnesso"); }} data-testid="logout-btn" className="btn-ghost inline-flex items-center gap-2 text-sm">
                <LogOut className="w-4 h-4" /><span className="hidden sm:inline">Esci</span>
              </button>
            ) : (
              <button onClick={() => setOpenLogin(true)} data-testid="open-login-btn" className="btn-brand inline-flex items-center gap-2 text-sm">
                <LogIn className="w-4 h-4" /> Accedi
              </button>
            )}
          </div>
        </div>

        {/* mobile nav */}
        <nav className="md:hidden mt-4 flex items-center justify-center gap-1 p-1.5 rounded-full bg-[hsl(var(--bg-2)/0.7)] border border-[hsl(var(--border-soft))] backdrop-blur" data-testid="topnav-mobile">
          {NAV.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActive(id)}
              data-testid={`nav-${id}-mobile`}
              className={`nav-pill ${active === id ? "nav-pill-active" : ""}`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </header>

      {/* ============== HERO ============== */}
      <main className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 pt-14 pb-24">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8 fade-up">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[hsl(var(--brand-300))] px-3 py-1 rounded-full border border-[hsl(var(--brand-500)/0.45)] bg-[hsl(var(--brand-500)/0.12)]">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--brand-400))] shadow-[0_0_12px_hsl(var(--brand-glow))]" />
              Server Roleplay
            </span>
            <h1 className="font-display title-stroke text-6xl sm:text-7xl lg:text-[5.5rem] leading-[0.95] mt-5">
              {greeting()},<br />
              <span className="brand-text" data-testid="player-nickname">
                {user ? `${nickname}` : "Accedi per visualizzare i tuoi dati"}
              </span>
            </h1>
            <p className="mt-5 text-base text-[hsl(var(--text-soft))] max-w-xl">
              Panoramica completa del tuo personaggio: portafoglio, lavoro, documenti e tempo di gioco — sincronizzati live con il tuo server Minecraft.
            </p>
          </div>

          {/* hero side card — Stato giocatore with avatar */}
          <div className="lg:col-span-4 fade-up delay-2">
            <div className="card-surface p-6" data-testid="status-card">
              <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--text-mute))]">
                Stato giocatore
              </div>
              <div className="mt-4 flex items-center gap-4">
                {/* PLAYER AVATAR (moved here from Generale) */}
                <div className="relative shrink-0" data-testid="player-head-wrap">
                  <img
                    src={avatar}
                    alt="player head"
                    className="w-14 h-14 rounded-lg border border-[hsl(var(--brand-500)/0.4)] shadow-[0_8px_24px_-8px_hsl(var(--brand-500)/0.7)]"
                    data-testid="player-head"
                  />
                  {user && (
                    <button
                      onClick={loadStats}
                      disabled={refreshing}
                      data-testid="refresh-stats-btn"
                      className="absolute -bottom-1.5 -right-1.5 inline-flex items-center justify-center w-6 h-6 rounded-md bg-[hsl(var(--bg-2))] border border-[hsl(var(--border-strong))] text-[hsl(var(--text-soft))] hover:text-white hover:border-[hsl(var(--brand-500))] transition disabled:opacity-40"
                      title="Ricarica dati"
                    >
                      <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
                    </button>
                  )}
                </div>
                <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                  <div className="text-2xl font-semibold truncate">{user ? nickname : "Ospite"}</div>
                  <span className={`text-[10px] font-bold tracking-wider px-2 py-1 rounded-md whitespace-nowrap ${user ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-amber-500/15 text-amber-300 border border-amber-500/30"}`}>
                    {user ? "ONLINE" : "OSPITE"}
                  </span>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg bg-[hsl(var(--bg-2))]/60 border border-[hsl(var(--border-soft))] px-3 py-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-mute))]">Conto</div>
                  <div className="text-white font-semibold mt-1 truncate">{conto || "—"}</div>
                </div>
                <div className="rounded-lg bg-[hsl(var(--bg-2))]/60 border border-[hsl(var(--border-soft))] px-3 py-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-mute))]">Lavoro</div>
                  <div className="text-white font-semibold mt-1 truncate">{lavoro || "—"}</div>
                </div>
              </div>
              {!user && (
                <button onClick={() => setOpenLogin(true)} data-testid="cta-login-btn" className="btn-brand w-full justify-center inline-flex items-center gap-2 mt-5">
                  <LogIn className="w-4 h-4" /> Accedi adesso
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ============== GENERALE ============== */}
        {active === "home" && (
          <section className="mt-20" data-testid="section-generale">
            <SectionHead title="Generale" subtitle="Panoramica generale di gioco" icon={IdCard} />

            {/* 6 tiles: 3 above + 3 below — aligned 3x2 grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-7">
              <StatTile icon={CircleDollarSign} label="Conto" value={conto} tint="amber" testid="stat-conto" />
              <StatTile icon={Fish} label="Aragoste" value={aragoste} tint="cyan" testid="stat-aragoste" />
              <StatTile icon={Phone} label="Telefono" value={telefono} tint="slate" testid="stat-telefono" />
              <StatTile icon={ScrollText} label="Documenti" value={docDisplay} tint="blue" testid="stat-documenti" />
              <StatTile icon={Calendar} label="Tempo (7gg)" value={tempo} tint="slate" testid="stat-tempo" />
              <StatTile icon={Briefcase} label="Lavoro" value={lavoro} tint="slate" testid="stat-lavoro" />
            </div>
          </section>
        )}

        {/* ============== CONTO CORRENTE ============== */}
        {active === "conto" && <ContoCorrente stats={stats} user={user} onLogin={() => setOpenLogin(true)} />}

        {/* ============== VITA LAVORATIVA ============== */}
        {active === "lavoro" && <VitaLavorativa stats={stats} user={user} onLogin={() => setOpenLogin(true)} />}

        {/* ============== FEDINA PENALE ============== */}
        {active === "fedina" && <FedinaPenale stats={stats} />}

        <footer className="mt-16 text-center text-xs text-[hsl(var(--text-mute))]">
          {stats && <>Sorgente dati: <span className="text-[hsl(var(--brand-300))]">{stats.source}</span> · </>}
          configura il plugin per i dati live (vedi <span className="text-white">minecraft-plugin/</span> nello zip)
        </footer>
      </main>

      <LoginModal open={openLogin} onOpenChange={setOpenLogin} />
    </div>
  );
}
